import { DisplayableProduct, ModelBud, ProductModel, UpsellPage } from "../../../../store/api/upsellApi";
import React, { ChangeEvent, useMemo, useState } from "react";
import { useSalesareaCtx } from "../../modernTS/salesarea/SalesareaContext";
import { useWatch } from "react-hook-form";
import useAddProducts, { NO_PRODUCT_SELECTED } from "../../modernTS/JUI/Products/useAddProducts";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
} from "@mui/material";
import { Add, Delete, DragIndicator, KeyboardArrowDown } from "@mui/icons-material";
import { TranslatableTextField } from "../../modernTS/JUI/Forms/TranslatableTextField";
import { createPortal } from "react-dom";
import { useApplicationDefaultLanguage } from "../../../../../utils/languageHooks";
import { TranslationsStorage } from "../../../../components/TranslatableEditor/TranslatableEditor.d";
import { Products } from "../../modernTS/JUI/Products/Products";
import { Product } from "../../modernTS/JUI/Products/Product";
import { grey } from "@mui/material/colors";
import { useConfirmDeletion } from "../../modernTS/JUI/confirmDeletion/useConfirmDeletion";
import { DndContext, DragOverlay, UniqueIdentifier } from "@dnd-kit/core";
import { rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToWindowEdges } from "@dnd-kit/modifiers";
import { dropAnimationConfig } from "../../../Producten/ImageUuids";
import { CSS } from "@dnd-kit/utilities";
import JTooltip from "../../../../../../Components/JTooltip";

// const useDraggableInPortal = () => {
//   const element = useRef<HTMLDivElement>(document.createElement("div")).current;
//
//   useEffect(() => {
//     if (element) {
//       element.style.pointerEvents = "none";
//       element.style.position = "absolute";
//       element.style.height = "100%";
//       element.style.width = "100%";
//       element.style.top = "0";
//
//       document.body.appendChild(element);
//
//       return () => {
//         document.body.removeChild(element);
//       };
//     }
//   }, [element]);
//
//   return function (render: (provided: DraggableProvided, snapshot: DraggableStateSnapshot) => ReactElement) {
//     return function (provided: DraggableProvided, snapshot: DraggableStateSnapshot) {
//       const result = render(provided, snapshot);
//       const style = provided.draggableProps.style as DraggingStyle;
//       if (style.position === "fixed") {
//         return createPortal(result, element);
//       }
//       return result;
//     };
//   };
// };

function DraggablePage({
  page,
  index,
  pagesFieldArrayName,
  startOpen = false,
  onProductsAdded,
  onProductsRemoved,
  onPageDeleted,
  onTextChanged,
}: {
  index: number;
  page: ModelBud<UpsellPage>;
  pagesFieldArrayName?: string;
  startOpen?: boolean;
  onProductsAdded: (products: DisplayableProduct[], pageIndex: number) => void;
  onProductsRemoved: (productIndices: number[], pageIndex: number) => void;
  onPageDeleted: (page: { id: ModelBud<UpsellPage>["id"] | null; pageIndex: number }) => void;
  onTextChanged?: (newText: TranslationsStorage, fieldName: "name" | "description", pageIndex: number) => void;
}) {
  const [open, setOpen] = useState(startOpen);
  const baseKey: "pages" | `${string}.pages` = pagesFieldArrayName ? `${pagesFieldArrayName}.pages` : "pages";
  const { menuSalesareaId } = useSalesareaCtx();

  const nameKey = `${baseKey}.${index}.name`;
  const name = useWatch({ name: nameKey });
  const products: ProductModel[] = useWatch({ name: `${baseKey}.${index}.products` });
  const openProductSelector = useAddProducts({ allowMultiple: true, menuSalesareaId });
  const lang = useApplicationDefaultLanguage();

  const nameInCurrentLanguage = name[lang]?.name ?? "No name yet";

  const [selectedProducts, setSelectedProducts] = useState<ProductModel["id"][]>([]);

  const confirmDeletion = useConfirmDeletion();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: page.id });

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: 1,
      }
    : undefined;

  return (
    <Box ref={setNodeRef} style={style} {...attributes}>
      <ListItemButton onClick={() => setOpen((state) => !state)}>
        <JTooltip title={"Drag to reorder"}>
          <ListItemIcon sx={{ "&:hover": { cursor: "grab" } }} {...listeners}>
            <DragIndicator />
          </ListItemIcon>
        </JTooltip>
        <ListItemText secondary={`page ${index + 1}`}>
          {nameInCurrentLanguage} - <span>{products.length} products</span>
        </ListItemText>
        <ListItemSecondaryAction>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <KeyboardArrowDown
              sx={{ transform: open ? "rotate(-180deg)" : "", transition: "transform 200ms ease-in" }}
            />
            <IconButton
              onClick={async (e) => {
                e.stopPropagation();
                const confirmed = await confirmDeletion(<>Are you sure you want to delete page {index + 1}?</>);
                if (confirmed) {
                  onPageDeleted({ pageIndex: index, id: page.id });
                }
              }}
              size={"small"}
              sx={(theme) => ({
                ml: 2,
                color: grey[400],
                transition: "color 200ms ease-in-out",
                "&:hover": {
                  color: theme.palette.error.main,
                },
              })}
            >
              <Delete
                sx={{
                  color: "inherit",
                }}
              />
            </IconButton>
          </Box>
        </ListItemSecondaryAction>
      </ListItemButton>

      <Collapse in={open}>
        <Box sx={{ ml: 5, pl: 4, background: "background.paper", gap: 2, display: "grid" }}>
          <Box
            sx={{
              display: "grid",
              gap: 4,
            }}
          >
            <Box>
              <FormLabel sx={{ mb: 2, display: "inline-block" }}>Page name</FormLabel>
              <TranslatableTextField
                name={nameKey}
                onUpdated={(newTranslations) => {
                  onTextChanged?.(newTranslations, "name", index);
                }}
                placeholder={"Would you like a drink with that?"}
                translatableText={"name"}
              />
            </Box>
            <Box>
              <FormLabel sx={{ mb: 2, display: "inline-block" }}>Page description</FormLabel>
              <TranslatableTextField
                multiline
                name={`${baseKey}.${index}.description`}
                onUpdated={(newTranslations) => {
                  onTextChanged?.(newTranslations, "description", index);
                }}
                placeholder={"Optional description"}
                rows={3}
                translatableText={"description"}
              />
            </Box>

            <Box>
              <Box sx={{ display: "inline-flex", mb: 2, width: "100%", justifyContent: "space-between" }}>
                <FormLabel>Products to be shown</FormLabel>
                <Button
                  color={"error"}
                  disabled={selectedProducts.length === 0}
                  onClick={() => {
                    onProductsRemoved(selectedProducts, index);
                    setSelectedProducts([]);
                  }}
                  size={"small"}
                  sx={{ height: "fit-content" }}
                  variant={selectedProducts.length === 0 ? "outlined" : "contained"}
                >
                  Remove products ({selectedProducts.length})
                </Button>
              </Box>

              <Products
                allowAdd
                onAdd={async () => {
                  const products = await openProductSelector();
                  if (products !== NO_PRODUCT_SELECTED) {
                    onProductsAdded(products, index);
                  }
                }}
                products={products}
              >
                {(p) => (
                  <Product
                    key={p.id}
                    leftAction={
                      <Box
                        sx={{
                          "&:hover": { cursor: "grab" },
                          color: grey[500],
                        }}
                      >
                        <DragIndicator fontSize={"small"} />
                      </Box>
                    }
                    product={p}
                    rightAction={
                      <Checkbox
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          if (event.target.checked) {
                            setSelectedProducts((all) => [...all, p.id]);
                          } else {
                            setSelectedProducts((all) => [...all.filter((id) => id !== p.id)]);
                          }
                        }}
                        size={"small"}
                        sx={{
                          color: grey["400"],
                          padding: 0.5,
                        }}
                      />
                    }
                  />
                )}
              </Products>
            </Box>
          </Box>
        </Box>
      </Collapse>
      <Divider sx={{ my: 1 }} />
    </Box>
  );
}

export function UpsellFlowPages({
  droppableId,
  hidden,
  pages,
  onAppend,
  onMove,
  onProductsAdded,
  onProductsRemoved,
  onTextChanged,
  fieldArrayNamePrefix,
  onPageDeleted,
  startOpen = false,
}: {
  droppableId: string;
  hidden: boolean;
  pages: ModelBud<UpsellPage>[];
  onAppend: () => void;
  onMove: (from: number, to: number) => void;
  onProductsAdded: (products: DisplayableProduct[], pageIndex: number) => void;
  onProductsRemoved: (productIds: ProductModel["id"][], pageId: number) => void;
  onPageDeleted: (page: { id: UpsellPage["id"]; pageIndex: number }) => void;
  onTextChanged?: (newText: TranslationsStorage, fieldName: "name" | "description", pageIndex: number) => void;
  fieldArrayNamePrefix?: string;
  startOpen?: boolean;
}) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const getIndex = (id: UniqueIdentifier) => pages.findIndex((page) => page.id == id);
  const activeIndex = useMemo(() => {
    return activeId != null ? getIndex(activeId) : null;
  }, [activeId, getIndex]);
  return (
    <Collapse in={!hidden} mountOnEnter orientation={"vertical"}>
      <Box role={"tabpanel"}>
        <DndContext
          modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
          onDragEnd={({ over }) => {
            if (over?.id && activeId) {
              onMove(getIndex(activeId), getIndex(over.id));
            }
          }}
          onDragStart={({ active }) => {
            setActiveId(active.id);
          }}
        >
          <SortableContext items={pages} strategy={rectSortingStrategy}>
            <List subheader={<ListSubheader>Pages</ListSubheader>}>
              {pages.map((page, index) => (
                <DraggablePage
                  index={index}
                  key={page.id}
                  onPageDeleted={onPageDeleted}
                  onProductsAdded={onProductsAdded}
                  onProductsRemoved={onProductsRemoved}
                  onTextChanged={onTextChanged}
                  page={page}
                  pagesFieldArrayName={fieldArrayNamePrefix}
                  startOpen={startOpen}
                />
              ))}

              <ListItemButton onClick={() => onAppend()}>
                <ListItemIcon>
                  <Add />
                </ListItemIcon>
                <ListItemText>Add another page</ListItemText>
              </ListItemButton>
            </List>
          </SortableContext>
          {createPortal(
            <DragOverlay adjustScale={false} dropAnimation={dropAnimationConfig}>
              {activeIndex !== null ? (
                <DraggablePage
                  index={activeIndex}
                  onPageDeleted={onPageDeleted}
                  onProductsAdded={onProductsAdded}
                  onProductsRemoved={onProductsRemoved}
                  onTextChanged={onTextChanged}
                  page={pages[activeIndex]}
                  pagesFieldArrayName={fieldArrayNamePrefix}
                  startOpen={startOpen}
                />
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      </Box>
    </Collapse>
  );
}
